import { useState, useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight, Mail, Play, MapPin, Scan, LockOpen, VolumeX, Wrench, CreditCard, Banknote, ChevronUp, Linkedin, Download } from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";
import VideoModal from "@/components/VideoModal";
import DeckEmailGate from "@/components/DeckEmailGate";
import { supabase } from "@/integrations/supabase/client";

import gallery2 from "@/assets/deck/gallery-2.png";
import casestudy2 from "@/assets/deck/casestudy-2.jpg";
import casestudy3 from "@/assets/deck/casestudy-3.jpg";
import casestudy4 from "@/assets/deck/casestudy-4.jpg";
import podInSitu from "@/assets/deck/pod-in-situ.jpeg";
import tflLogo from "@/assets/deck/tfl-logo.png";
import slide1Bg from "@/assets/deck/slide1-bg.jpg";
import qwipFounders from "@/assets/deck/qwip-founders.jpg";
// archived: import qwipPodBooth from "@/assets/pod-design.png";
// archived: import redPhoneBooth from "@/assets/deck/red-phone-booth.png";

// ─── Shared Components ──────────────────────────────────────────
const QwipLogo = ({ light = false }: { light?: boolean }) => (
  <div className={`absolute top-6 right-6 z-20 font-funnel font-bold text-xl ${light ? "text-white" : "text-foreground"}`}>
    qwip
  </div>
);

const MobileImagePeek = ({ src, alt }: { src: string; alt: string }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="laptop:hidden desktop:hidden absolute bottom-0 left-0 right-0 z-10 pointer-events-none">
      <motion.div
        initial={{ y: 320 }}
        animate={{ y: expanded ? 0 : 255 }}
        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        className="pointer-events-auto"
      >
        <div className="flex justify-center pb-1 cursor-pointer" onClick={() => setExpanded(!expanded)}>
          <motion.div animate={{ y: [0, -4, 0] }} transition={{ repeat: Infinity, duration: 1.5 }}>
            <ChevronUp className={`w-5 h-5 text-foreground/40 transition-transform ${expanded ? "rotate-180" : ""}`} />
          </motion.div>
        </div>
        <img
          src={src}
          alt={alt}
          className={`w-full h-72 object-cover rounded-t-2xl shadow-xl transition-opacity ${expanded ? "opacity-100" : "opacity-60"}`}
        />
      </motion.div>
    </div>
  );
};
// ─── Slide Components ────────────────────────────────────────────

const SlideIntro = () => {
  const [videoOpen, setVideoOpen] = useState(false);

  return (
    <div
      className="flex flex-col items-center justify-center h-full text-center px-8 laptop:px-24 desktop:px-32 relative"
      style={{ backgroundImage: `url(${slide1Bg})`, backgroundSize: "cover", backgroundPosition: "center" }}
    >
      <div className="absolute inset-0 bg-black/20" />
      <motion.h1
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4, duration: 0.7 }}
        className="font-funnel font-bold text-7xl tablet:text-8xl laptop:text-9xl desktop:text-10xl text-white leading-[0.9] tracking-tight mb-8 relative z-10"
      >
        qwip
      </motion.h1>
      <motion.p
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.7 }}
        className="text-xl tablet:text-2xl laptop:text-2xl text-white/80 max-w-2xl font-funnel leading-relaxed mb-10 relative z-10"
      >
        the public phone booth, upgraded.
      </motion.p>
      <motion.button
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1 }}
        onClick={() => setVideoOpen(true)}
        className="inline-flex items-center gap-3 border border-white/30 text-white px-6 py-3 rounded-full font-sans font-medium text-sm hover:bg-white hover:text-foreground transition-all relative z-10"
      >
        <Play className="w-4 h-4" />
        watch our launch video
      </motion.button>
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4 }}
        className="text-sm text-white/50 font-sans mt-6 relative z-10"
      >
        &nbsp;swipe right to learn more →
      </motion.p>
      <VideoModal isOpen={videoOpen} onClose={() => setVideoOpen(false)} />
    </div>
  );
};

/*
// ─── ARCHIVED: Slide 2 (Booth Comparison) ────────────────────────
const CitySkylineSVG = () => ( ... );
const BeforeAfterSlider = () => ( ... );
const SlideBoothComparison = () => ( ... );
*/

const SlideWhoWeAre = () => (
  <div className="grid grid-cols-1 laptop:grid-cols-2 desktop:grid-cols-2 h-full">
    <div className="flex flex-col justify-center px-8 laptop:px-16 desktop:px-20 py-12">
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2 }}
        className="font-funnel font-bold text-2xl text-primary mb-4"
      >
        qwip
      </motion.p>
      <motion.h2
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="font-funnel text-4xl tablet:text-5xl laptop:text-5xl desktop:text-6xl text-foreground leading-[0.95] tracking-tight mb-8"
      >
        We place <span className="font-bold">private, soundproof pods</span> in <span className="font-bold">public spaces</span>.
      </motion.h2>
      <motion.p
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="text-lg text-muted-foreground font-funnel leading-relaxed max-w-lg mb-2"
      >
        your visitors get a private place to take <strong>calls</strong>, <strong>work</strong>, or have a moment of <strong>quiet</strong> - without having to leave your space.
      </motion.p>
      <motion.p
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
        className="text-lg text-muted-foreground font-funnel leading-relaxed max-w-lg"
      >
        You get paid every time they book.
      </motion.p>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.7 }}
        className="mt-10 grid grid-cols-3 gap-4 max-w-lg"
      >
        {[
          "zero upfront cost",
          "up to £3,500/month revenue",
          "increase retention and stand out",
        ].map((text, i) => (
          <motion.div
            key={text}
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 + i * 0.12 }}
            className="border border-foreground/15 rounded-2xl p-5 flex items-center justify-center text-center backdrop-blur-sm"
          >
            <p className="font-funnel text-base font-bold leading-snug text-foreground">{text}</p>
          </motion.div>
        ))}
      </motion.div>
    </div>
    <div className="hidden laptop:flex desktop:flex items-center justify-center p-8">
      <motion.img
        initial={{ opacity: 0, y: 60 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        src={podInSitu}
        alt="qwip pod in a café"
        className="w-full h-full object-cover rounded-3xl max-h-[80vh] shadow-xl"
      />
    </div>
    {/* Mobile image peek */}
    <MobileImagePeek src={podInSitu} alt="qwip pod in a café" />
  </div>
);

const kgxTestimonials = [
  { quote: "I had to catch my Eurostar train in an hour, and had nowhere private to take an important client call. I was so close to cancelling that meeting and this was exactly what I needed in the moment.", author: "Adnaan, Consultant" },
  { quote: "I need this EVERY DAY. I work from cafes, hotel lobbies, wherever I can find a seat, but I always struggle with the noise.", author: "Rachael, 24, Law Student" },
  { quote: "I hope these pop up everywhere. As a parent who works remotely, this would make my life so much easier.", author: "Mark, 39, Brand Agency Owner" },
];

const SlideKingsX = () => {
  const [testimonialIndex, setTestimonialIndex] = useState(0);
  const [carouselIndex, setCarouselIndex] = useState(0);
  const carouselImages = [qwipFounders, gallery2];

  useEffect(() => {
    const timer = setInterval(() => {
      setCarouselIndex((prev) => (prev + 1) % carouselImages.length);
    }, 3000);
    return () => clearInterval(timer);
  }, []);

  return (
    <div className="grid grid-cols-1 laptop:grid-cols-2 desktop:grid-cols-2 h-full">
      {/* Left column */}
      <div className="flex flex-col justify-center px-8 laptop:px-16 desktop:px-20 py-3">
        {/* Logo lockup: qwip | TfL */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="flex items-center gap-6 mb-3"
        >
          <span className="font-funnel font-bold text-4xl laptop:text-6xl desktop:text-6xl text-foreground">qwip</span>
          <div className="w-px h-14 laptop:h-16 desktop:h-16 bg-foreground/20" />
          <img src={tflLogo} alt="Transport for London" className="h-14 laptop:h-20 desktop:h-20 object-contain" />
        </motion.div>

        {/* Location */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.25 }}
          className="flex items-center gap-2 mb-4"
        >
          <MapPin className="w-4 h-4 text-muted-foreground" />
          <span className="font-sans text-sm text-muted-foreground font-medium">King's Cross Station</span>
        </motion.div>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35 }}
          className="text-base text-muted-foreground font-funnel leading-relaxed max-w-lg mb-4"
        >
          this january, we trialled our first pod at King's Cross Station - the busiest transport hub in the UK.<br /><br />the results spoke for themselves:
        </motion.p>

        {/* Stats */}
        <div className="flex gap-3 mb-4 max-w-lg">
          {[
            { value: ">£100", label: "revenue in a single day" },
            { value: "~60%", label: "utilisation rate" },
          ].map((item, i) => (
            <motion.div
              key={item.label}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 + i * 0.12 }}
              className="flex-1 bg-foreground text-primary-foreground rounded-2xl p-4"
            >
              <p className="font-funnel font-bold text-2xl laptop:text-3xl mb-1">{item.value}</p>
              <p className="text-primary-foreground/70 font-sans text-xs">{item.label}</p>
            </motion.div>
          ))}
        </div>

        {/* Londoners want qwip */}
        <motion.p
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.75 }}
          className="font-funnel font-bold text-xl text-foreground mb-3"
        >
          londoners want qwip.
        </motion.p>

        {/* Testimonial — card style */}
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.85 }}
          className="max-w-lg"
        >
          <AnimatePresence mode="wait">
            <motion.div
              key={testimonialIndex}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.3 }}
              className="bg-foreground/5 border border-foreground/10 rounded-2xl p-5"
            >
              <p className="font-sans text-foreground text-base leading-relaxed mb-3">
                "{kgxTestimonials[testimonialIndex].quote}"
              </p>
              <p className="font-sans text-xs text-muted-foreground font-semibold uppercase tracking-wider">
                {kgxTestimonials[testimonialIndex].author}
              </p>
            </motion.div>
          </AnimatePresence>
          <div className="flex gap-2 mt-4">
            {kgxTestimonials.map((_, i) => (
              <button
                key={i}
                onClick={(e) => { e.stopPropagation(); setTestimonialIndex(i); }}
                className={`w-2 h-2 rounded-full transition-all duration-300 ${
                  i === testimonialIndex ? "bg-foreground w-5" : "bg-foreground/20 hover:bg-foreground/40"
                }`}
              />
            ))}
          </div>
        </motion.div>
      </div>

      {/* Right column — single tall image (gallery2) */}
      <div className="hidden laptop:flex desktop:flex items-center justify-center p-6">
        <motion.div
          initial={{ opacity: 0, y: 80 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="relative w-full h-full rounded-3xl overflow-hidden"
        >
          <AnimatePresence mode="wait">
            <motion.img
              key={carouselIndex}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.6 }}
              src={carouselImages[carouselIndex]}
              alt="qwip pod at King's Cross"
              className="absolute inset-0 w-full h-full object-cover"
            />
          </AnimatePresence>
        </motion.div>
      </div>
      {/* Mobile image peek */}
      <MobileImagePeek src={qwipFounders} alt="qwip founders at King's Cross" />
    </div>
  );
};

const SlideCaseStudy = () => (
  <div className="flex flex-col h-full px-8 laptop:px-16 desktop:px-20 py-12 justify-center bg-primary relative">
    <QwipLogo light />
    <motion.h2
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.3 }}
      className="font-funnel font-bold text-4xl tablet:text-5xl laptop:text-5xl desktop:text-6xl text-white leading-[0.95] tracking-tight mb-12"
    >
      other cities upgraded the phone booth.<br />
      <span className="font-cormorant italic font-normal">we're bringing it home.</span>
    </motion.h2>
    <div className="grid grid-cols-1 gap-6 laptop:grid-cols-3 desktop:grid-cols-3 laptop:gap-8 desktop:gap-8">
      {[
        { img: casestudy2, country: "Japan", stat: "400+", description: "booths deployed across Japanese railway stations" },
        { img: casestudy3, country: "United States", stat: "50+", description: "booths across hotel lobbies and airports" },
        { img: casestudy4, country: "Middle East", stat: "30+", description: "booths across shopping centres, cafés, and fitness clubs" },
      ].map((item, i) => (
        <motion.div
          key={item.country}
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 + i * 0.15 }}
          className="flex flex-row laptop:flex-col desktop:flex-col gap-4 items-center laptop:items-start desktop:items-start"
        >
          <div className="w-20 h-20 laptop:w-full laptop:h-auto laptop:aspect-[4/3] desktop:w-full desktop:h-auto desktop:aspect-[4/3] rounded-2xl overflow-hidden flex-shrink-0">
            <img src={item.img} alt={item.country} className="w-full h-full object-cover" />
          </div>
          <div>
            <div className="flex items-center gap-2 mb-1 laptop:mb-2">
              <MapPin className="w-4 h-4 text-white/60" />
              <p className="font-funnel font-bold text-white text-lg">{item.country}</p>
            </div>
            <p className="text-white/70 text-sm font-funnel leading-relaxed">{item.stat} {item.description}</p>
          </div>
        </motion.div>
      ))}
    </div>
  </div>
);

const HoverVideoStep = ({ step, index, direction, activeIndex, onVideoEnd }: { step: { icon: any; title: string; description: string; video: string }; index: number; direction: "left" | "right"; activeIndex: number; onVideoEnd: () => void }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const isActive = activeIndex === index;

  useEffect(() => {
    if (videoRef.current) {
      if (isActive) {
        videoRef.current.currentTime = 0;
        videoRef.current.play();
      } else {
        videoRef.current.pause();
      }
    }
  }, [isActive]);

  return (
    <motion.div
      initial={{ opacity: 0, x: direction === "left" ? -20 : 20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: 0.4 + index * 0.15 }}
      className="flex gap-4 items-start rounded-2xl p-3 -mx-3"
    >
      <div className="flex-shrink-0 w-12 h-12 rounded-2xl bg-foreground text-primary-foreground flex items-center justify-center">
        <step.icon className="w-5 h-5" />
      </div>
      <div className="flex-1">
        <p className="font-funnel font-bold text-lg text-foreground mb-1">{step.title}</p>
        <p className="text-muted-foreground text-sm font-sans leading-relaxed whitespace-pre-line">{step.description}</p>
        <motion.div
          animate={{ height: isActive ? "auto" : 0, opacity: isActive ? 1 : 0 }}
          initial={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="overflow-hidden mt-3"
        >
          <div className="w-[150px] aspect-square rounded-xl overflow-hidden">
            <video
              ref={videoRef}
              muted
              playsInline
              preload="auto"
              onEnded={onVideoEnd}
              className="w-full h-full object-cover"
            >
              <source src={step.video} type="video/mp4" />
            </video>
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
};

const SlideHowItWorks = () => {
  const [activeVideoIndex, setActiveVideoIndex] = useState(-1);

  useEffect(() => {
    const timer = setTimeout(() => setActiveVideoIndex(0), 600);
    return () => clearTimeout(timer);
  }, []);

  const handleVideoEnd = () => {
    setActiveVideoIndex((prev) => {
      if (prev < 2) return prev + 1;
      return -1;
    });
  };

  const userSteps = [
    { icon: Scan, title: "scan", description: "user scans the QR code on the pod to start the booking process.", video: "/videos/step-scan.mp4" },
    { icon: LockOpen, title: "unlock", description: "they get an access code, enter it on the numpad, and unlock the pod.", video: "/videos/step-unlock.mp4" },
    { icon: VolumeX, title: "focus", description: "the pod is all theirs.\n\nthey're billed by the minute and charged at the end of their session.", video: "/videos/step-focus.mp4" },
  ];

  const hostSteps = [
    { icon: Wrench, title: "we install", description: "we deliver and install the pod (1m x 1m) in your space - zero cost, zero hassle." },
    { icon: CreditCard, title: "users pay", description: "visitors scan, book, and pay by the minute. all payments handled by us." },
    { icon: Banknote, title: "you earn", description: "we operate on a revenue share, or fixed rent model." },
  ];

  return (
    <div className="flex flex-col h-full relative overflow-y-auto">
      <div className="text-center pt-8 pb-1">
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="font-funnel font-bold text-2xl laptop:text-4xl desktop:text-5xl text-foreground tracking-tight"
        >
          how it works
        </motion.h2>
      </div>

      {/* Desktop: side-by-side grid */}
      <div className="hidden laptop:flex desktop:flex flex-1 items-center px-8 laptop:px-14 desktop:px-16">
        <div className="w-full grid grid-cols-[1fr_1px_1fr] gap-x-8 laptop:gap-x-12 desktop:gap-x-16">
          <div className="mb-4">
            <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}
              className="text-xs uppercase tracking-[0.25em] text-muted-foreground font-sans font-bold">for the user</motion.p>
          </div>
          <div />
          <div className="mb-4">
            <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}
              className="text-xs uppercase tracking-[0.25em] text-muted-foreground font-sans font-bold">for the host</motion.p>
          </div>

          {userSteps.map((step, i) => (
            <> 
              <HoverVideoStep key={step.title} step={step} index={i} direction="left" activeIndex={activeVideoIndex} onVideoEnd={handleVideoEnd} />
              <div className="bg-foreground/10" />
              <motion.div key={`host-${i}`} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.4 + i * 0.15 }} className="flex gap-4 items-start">
                <div className="flex-shrink-0 w-12 h-12 rounded-2xl bg-foreground text-primary-foreground flex items-center justify-center">
                  {(() => { const Icon = hostSteps[i].icon; return <Icon className="w-5 h-5" />; })()}
                </div>
                <div>
                  <p className="font-funnel font-bold text-lg text-foreground mb-1">{hostSteps[i].title}</p>
                  <p className="text-muted-foreground text-sm font-sans leading-relaxed">{hostSteps[i].description}</p>
                </div>
              </motion.div>
            </>
          ))}
        </div>
      </div>

      {/* Mobile: stacked cards */}
      <div className="laptop:hidden desktop:hidden flex-1 flex flex-col px-6 py-4 gap-4 overflow-y-auto">
        {/* User card with video autoplay */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
          className="bg-foreground/5 border border-foreground/10 rounded-2xl p-5">
          <p className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground font-sans font-bold mb-3">for the user</p>
          <div className="space-y-3">
            {userSteps.map((step, idx) => (
              <div key={step.title}>
                <div className="flex gap-3 items-start">
                  <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-foreground text-primary-foreground flex items-center justify-center">
                    <step.icon className="w-4 h-4" />
                  </div>
                  <div>
                    <p className="font-funnel font-bold text-base text-foreground">{step.title}</p>
                    <p className="text-muted-foreground text-xs font-sans leading-relaxed">{step.description.split('\n\n')[0]}</p>
                  </div>
                </div>
                <motion.div
                  animate={{ height: activeVideoIndex === idx ? "auto" : 0, opacity: activeVideoIndex === idx ? 1 : 0 }}
                  initial={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.3 }}
                  className="overflow-hidden mt-2 ml-13"
                >
                  <div className="w-24 aspect-square rounded-xl overflow-hidden">
                    <video
                      autoPlay={activeVideoIndex === idx}
                      muted
                      playsInline
                      preload="auto"
                      onEnded={handleVideoEnd}
                      className="w-full h-full object-cover"
                    >
                      <source src={step.video} type="video/mp4" />
                    </video>
                  </div>
                </motion.div>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Host card */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.45 }}
          className="bg-foreground/5 border border-foreground/10 rounded-2xl p-5">
          <p className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground font-sans font-bold mb-3">for the host</p>
          <div className="space-y-3">
            {hostSteps.map((step) => (
              <div key={step.title} className="flex gap-3 items-start">
                <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-foreground text-primary-foreground flex items-center justify-center">
                  <step.icon className="w-4 h-4" />
                </div>
                <div>
                  <p className="font-funnel font-bold text-base text-foreground">{step.title}</p>
                  <p className="text-muted-foreground text-xs font-sans leading-relaxed">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

const SlideRevenue = () => (
  <div className="flex flex-col h-full bg-primary relative overflow-y-auto">
    <QwipLogo light />
    {/* Desktop: centered layout */}
    <div className="hidden laptop:flex desktop:flex flex-col items-center justify-center flex-1 text-center px-16 desktop:px-24">
      <motion.h2
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="font-funnel font-bold text-5xl desktop:text-6xl text-white leading-[0.95] tracking-tight mb-4"
      >
        your space makes money<br />
        <span className="font-cormorant italic font-normal">whilst you sleep.</span>
      </motion.h2>
      <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }}
        className="text-lg text-white/70 font-sans mb-4">
        it costs you <span className="font-bold text-white">absolutely nothing.</span>
      </motion.p>
      <div className="w-16 h-px bg-white/20 mb-4" />
      <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.45 }}
        className="text-sm uppercase tracking-[0.3em] text-white/50 font-sans mb-10">
        what you can expect per pod
      </motion.p>
      <div className="grid grid-cols-3 gap-8 max-w-4xl w-full">
        {[
          { value: "£39,000", label: "annual revenue\nper pod" },
          { value: "4 in 5", label: "of users make an additional purchase on-site" },
          { value: "72%", label: "of users become repeat visitors" },
        ].map((item, i) => (
          <motion.div key={item.label} initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 + i * 0.15 }}
            className="bg-white text-foreground rounded-3xl p-10 flex flex-col items-center">
            <p className="font-funnel font-bold text-6xl mb-3">{item.value}</p>
            <p className="text-foreground/70 font-sans text-base font-medium whitespace-pre-line">{item.label}</p>
          </motion.div>
        ))}
      </div>
    </div>
    {/* Mobile: left-aligned, stacked */}
    <div className="laptop:hidden desktop:hidden flex flex-col justify-center flex-1 px-6 py-8">
      <motion.h2 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
        className="font-funnel font-bold text-3xl text-white leading-[0.95] tracking-tight mb-3">
        your space makes money<br />
        <span className="font-cormorant italic font-normal">whilst you sleep.</span>
      </motion.h2>
      <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }}
        className="text-base text-white/70 font-sans mb-2">
        it costs you <span className="font-bold text-white">absolutely nothing.</span>
      </motion.p>
      <div className="w-12 h-px bg-white/20 mb-2" />
      <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.45 }}
        className="text-[10px] uppercase tracking-[0.25em] text-white/50 font-sans mb-5">
        what you can expect per pod
      </motion.p>
      <div className="flex flex-col gap-3">
        {[
          { value: "£39,000", label: "annual revenue per pod" },
          { value: "4 in 5", label: "of users make an additional purchase on-site" },
          { value: "72%", label: "of users become repeat visitors" },
        ].map((item, i) => (
          <motion.div key={item.label} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 + i * 0.1 }}
            className="bg-white text-foreground rounded-2xl p-4 flex items-center gap-4">
            <p className="font-funnel font-bold text-2xl flex-shrink-0">{item.value}</p>
            <p className="text-foreground/70 font-sans text-sm font-medium">{item.label}</p>
          </motion.div>
        ))}
      </div>
    </div>
  </div>
);

const SlideUrgency = () => (
  <div className="flex flex-col items-center justify-center h-full text-center px-8 laptop:px-16 desktop:px-24 relative">
    <motion.h2
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.4, duration: 0.6 }}
      className="font-funnel font-bold text-4xl tablet:text-6xl laptop:text-7xl desktop:text-8xl text-foreground leading-[0.9] tracking-tight mb-6"
    >
      we have a limited<br />
      number of pods<br />
      <span className="font-cormorant italic font-normal">available now.</span>
    </motion.h2>
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ delay: 0.6, duration: 0.5 }}
      className="w-40 laptop:w-52 mb-6"
    >
      <video
        autoPlay
        muted
        loop
        playsInline
        className="w-full rounded-2xl"
      >
        <source src="/videos/how-it-works.mov" type="video/mp4" />
      </video>
    </motion.div>
    <motion.p
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: 0.7 }}
      className="text-base laptop:text-xl text-muted-foreground font-sans max-w-2xl leading-relaxed"
    >
      we're selectively partnering with a number of venues for our upcoming rollout.
    </motion.p>
    <motion.p
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: 0.85 }}
      className="text-base laptop:text-xl text-muted-foreground font-sans max-w-2xl leading-relaxed mt-3"
    >
      if your space qualifies, we'd love to talk.
    </motion.p>
    <motion.a
      href="/tech-specs"
      target="_blank"
      rel="noopener noreferrer"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: 1 }}
      className="text-sm text-primary underline underline-offset-4 font-sans mt-4 hover:text-foreground transition-colors"
    >
      view tech specs →
    </motion.a>
  </div>
);

const SlideContact = () => (
  <div className="flex flex-col items-center justify-center h-full text-center px-8 laptop:px-16 desktop:px-24 pb-16">
    <motion.h2
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.3, duration: 0.6 }}
      className="font-funnel font-bold text-6xl tablet:text-7xl laptop:text-8xl desktop:text-9xl text-foreground leading-[0.9] tracking-tight mb-6"
    >
      qwip
    </motion.h2>
    <motion.p
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: 0.5 }}
      className="text-lg laptop:text-xl text-muted-foreground font-sans mb-3 max-w-xl leading-relaxed"
    >
      we're reinventing the public phone booth for the 21st century.
    </motion.p>
    <motion.p
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: 0.6 }}
      className="text-lg laptop:text-xl text-foreground font-sans font-bold mb-10 laptop:mb-12 max-w-xl leading-relaxed"
    >
      do you want to be<br className="laptop:hidden" /> a part of it?
    </motion.p>
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.7 }}
      className="flex flex-col gap-4 items-center"
    >
      <a
        href="https://wa.me/447347474059"
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center gap-3 bg-foreground text-primary-foreground px-8 py-4 rounded-full font-sans font-semibold text-lg hover:opacity-90 transition-opacity"
      >
        <FaWhatsapp className="w-5 h-5" />
        get in touch
      </a>
      <a
        href="mailto:hello@qwip.co.uk"
        className="flex items-center gap-3 text-foreground font-sans font-medium text-base hover:opacity-70 transition-opacity"
      >
        <Mail className="w-4 h-4" />
        hello@qwip.co.uk
      </a>
      <div className="flex items-center gap-3 text-foreground font-sans font-medium text-base">
        <Linkedin className="w-4 h-4" />
        <span>founders:&nbsp;
          <a href="https://www.linkedin.com/in/zain-hussain" target="_blank" rel="noopener noreferrer" className="underline hover:opacity-70 transition-opacity">Zain</a>
          &nbsp;&amp;&nbsp;
          <a href="https://www.linkedin.com/in/stiofanfh/" target="_blank" rel="noopener noreferrer" className="underline hover:opacity-70 transition-opacity">Stiofán</a>
        </span>
      </div>
    </motion.div>
  </div>
);

// ─── Slides Array ────────────────────────────────────────────────

const slides = [
  SlideIntro,
  // SlideBoothComparison, // archived
  SlideWhoWeAre,
  SlideKingsX,
  SlideCaseStudy,
  SlideHowItWorks,
  SlideRevenue,
  SlideUrgency,
  SlideContact,
];

// ─── Deck Page ───────────────────────────────────────────────────

const Deck = ({ skipEmailGate = false }: { skipEmailGate?: boolean }) => {
  const [current, setCurrent] = useState(0);
  const [direction, setDirection] = useState(0);
  const [showGate, setShowGate] = useState(!skipEmailGate);
  const [viewId, setViewId] = useState("");
  const startTimeRef = useRef(Date.now());
  const slidesVisitedRef = useRef<Set<number>>(new Set([0]));

  const goTo = useCallback((index: number) => {
    if (index < 0 || index >= slides.length) return;
    setDirection(index > current ? 1 : -1);
    setCurrent(index);
    slidesVisitedRef.current.add(index);
  }, [current]);

  const next = useCallback(() => goTo(current + 1), [current, goTo]);
  const prev = useCallback(() => goTo(current - 1), [current, goTo]);

  // Heartbeat: update duration and slides every 10s
  useEffect(() => {
    if (!viewId) return;
    const interval = setInterval(async () => {
      const duration = Math.round((Date.now() - startTimeRef.current) / 1000);
      const slidesArr = Array.from(slidesVisitedRef.current);
      await supabase
        .from("deck_views")
        .update({
          last_active_at: new Date().toISOString(),
          duration_seconds: duration,
          slides_viewed: slidesArr,
          total_slides_viewed: slidesArr.length,
        })
        .eq("id", viewId);
    }, 10000);
    return () => clearInterval(interval);
  }, [viewId]);

  // Final update on unmount
  useEffect(() => {
    if (!viewId) return;
    const handleUnload = () => {
      const duration = Math.round((Date.now() - startTimeRef.current) / 1000);
      const slidesArr = Array.from(slidesVisitedRef.current);
      navigator.sendBeacon?.(
        `https://pxurtuktvoeaytlozzue.supabase.co/rest/v1/deck_views?id=eq.${viewId}`,
        JSON.stringify({
          last_active_at: new Date().toISOString(),
          duration_seconds: duration,
          slides_viewed: slidesArr,
          total_slides_viewed: slidesArr.length,
        })
      );
    };
    window.addEventListener("beforeunload", handleUnload);
    return () => window.removeEventListener("beforeunload", handleUnload);
  }, [viewId]);

  const handleEmailSubmit = (_email: string, id: string) => {
    setViewId(id);
    startTimeRef.current = Date.now();
    setShowGate(false);
  };

  // Keyboard navigation
  useEffect(() => {
    if (showGate) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowRight" || e.key === " ") { e.preventDefault(); next(); }
      if (e.key === "ArrowLeft") { e.preventDefault(); prev(); }
      if (e.key === "f" || e.key === "F") {
        if (!document.fullscreenElement) {
          document.documentElement.requestFullscreen?.();
        } else {
          document.exitFullscreen?.();
        }
      }
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [next, prev, showGate]);

  // Touch/swipe
  useEffect(() => {
    if (showGate) return;
    let startX = 0;
    const handleTouchStart = (e: TouchEvent) => { startX = e.touches[0].clientX; };
    const handleTouchEnd = (e: TouchEvent) => {
      const diff = startX - e.changedTouches[0].clientX;
      if (Math.abs(diff) > 50) {
        diff > 0 ? next() : prev();
      }
    };
    window.addEventListener("touchstart", handleTouchStart);
    window.addEventListener("touchend", handleTouchEnd);
    return () => {
      window.removeEventListener("touchstart", handleTouchStart);
      window.removeEventListener("touchend", handleTouchEnd);
    };
  }, [next, prev, showGate]);

  const CurrentSlide = slides[current];
  const isDarkSlide = [0, 3, 5].includes(current);
  const arrowBg = isDarkSlide ? "bg-white/20 hover:bg-white/40" : "bg-foreground/10 hover:bg-foreground/20";
  const arrowIcon = isDarkSlide ? "text-white" : "text-foreground";
  const dotActive = isDarkSlide ? "bg-white" : "bg-foreground";
  const dotInactive = isDarkSlide ? "bg-white/50 hover:bg-white/70" : "bg-foreground/20 hover:bg-foreground/40";

  const variants = {
    enter: { opacity: 0 },
    center: { opacity: 1 },
    exit: { opacity: 0 },
  };

  return (
    <div className="h-screen w-screen bg-background overflow-hidden relative select-none">
      {/* Preload videos */}
      <link rel="preload" href="/videos/qwip-launch.mp4" as="video" type="video/mp4" />
      <link rel="preload" href="/videos/how-it-works.mov" as="video" type="video/mp4" />
      <link rel="preload" href="/videos/step-scan.mp4" as="video" type="video/mp4" />
      <link rel="preload" href="/videos/step-unlock.mp4" as="video" type="video/mp4" />
      <link rel="preload" href="/videos/step-focus.mp4" as="video" type="video/mp4" />

      {/* Email gate */}
      {showGate && <DeckEmailGate onSubmit={handleEmailSubmit} deckType="sales" />}

      {/* Slide content */}
      <AnimatePresence mode="wait" custom={direction}>
        <motion.div
          key={current}
          custom={direction}
          variants={variants}
          initial="enter"
          animate="center"
          exit="exit"
          transition={{ duration: 0.6, ease: "easeInOut" }}
          className="absolute inset-0"
        >
          <CurrentSlide />
        </motion.div>
      </AnimatePresence>

      {/* Progress bar */}
      <div className="absolute bottom-0 left-0 right-0 h-1 bg-foreground/5">
        <motion.div
          className="h-full bg-foreground"
          animate={{ width: `${((current + 1) / slides.length) * 100}%` }}
          transition={{ duration: 0.4 }}
        />
      </div>

      {/* Navigation */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-4">
        <div className="flex gap-2">
          {slides.map((_, i) => (
            <button
              key={i}
              onClick={() => goTo(i)}
              className={`w-2 h-2 rounded-full transition-all duration-300 ${
                i === current ? `${dotActive} w-6 shadow-md` : dotInactive
              }`}
            />
          ))}
        </div>
      </div>

      {/* Arrow buttons */}
      {current > 0 && (
        <button
          onClick={prev}
          className={`absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full ${arrowBg} flex items-center justify-center transition-colors backdrop-blur-sm`}
        >
          <ChevronLeft className={`w-5 h-5 ${arrowIcon}`} />
        </button>
      )}
      {current < slides.length - 1 && (
        <button
          onClick={next}
          className={`absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full ${arrowBg} flex items-center justify-center transition-colors backdrop-blur-sm`}
        >
          <ChevronRight className={`w-5 h-5 ${arrowIcon}`} />
        </button>
      )}

      {/* Slide counter + download */}
      <div className="absolute top-6 right-6 flex items-center gap-3">
        <button
          onClick={() => window.open("/deck-print", "_blank")}
          className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${isDarkSlide ? "bg-white/10 hover:bg-white/20 text-white/50 hover:text-white" : "bg-foreground/5 hover:bg-foreground/10 text-muted-foreground/50 hover:text-foreground"}`}
          title="Download as PDF"
        >
          <Download className="w-3.5 h-3.5" />
        </button>
        <span className="text-xs text-muted-foreground/50 font-sans">
          {current + 1} / {slides.length}
        </span>
      </div>
    </div>
  );
};

export default Deck;
